/**
 * SSN Decryption & Masking Utility
 *
 * Implements the exact decryption + locale-aware obfuscation pipeline used
 * by the legacy Rails back-end for customer SSN display.
 *
 * ─── Decryption (SsnEncryption.decode) ────────────────────────────────────
 *  1. Parse the hex-encoded ciphertext from `ssn_encrypted` into a byte array.
 *  2. Derive an 8-byte DES key + 8-byte IV from the fixed passkey and salt
 *     using OpenSSL's EVP_BytesToKey (MD5, 17 iterations — PBEWithMD5AndDES).
 *  3. Decrypt with DES-CBC + PKCS#5 unpadding (synchronous pure-JS DES).
 *  4. Decode the result as UTF-8 → plain SSN string.
 *
 * ─── Masking (Customer.obfuscate_ssn / SSN_OBFUSCATOR) ───────────────────
 *  idx    = length - 4
 *  result = '*'.repeat(idx) + last4chars
 *  Edge cases:
 *    US locale  + length ≤ 4 → shown as-is
 *    FI/default + length ≤ 4 → '*'.repeat(length)
 *
 * ─── Locale singleton ────────────────────────────────────────────────────
 *  Call setSiteLocale() from RuntimeConfigProvider after config loads.
 *  All getObfuscatedSSN() calls will use the configured locale.
 */

import { logger } from '@/shared/lib/logger';

// ---------------------------------------------------------------------------
// Public constants
// ---------------------------------------------------------------------------

/** Displayed in the SSN column when decryption fails. */
export const SSN_DECRYPT_ERROR_MARKER = '<<ERROR>>';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Allocates a new Uint8Array backed by a plain ArrayBuffer of the given length. */
function newBuf(length: number): Uint8Array<ArrayBuffer> {
  return new Uint8Array(new ArrayBuffer(length));
}

// ---------------------------------------------------------------------------
// MD5 (synchronous, RFC 1321)
// Used exclusively for EVP_BytesToKey key derivation — not for security.
// ---------------------------------------------------------------------------

function md5(input: Uint8Array): Uint8Array<ArrayBuffer> {
  const msgLen = input.length;
  const bitLen = msgLen * 8;

  // Pad to 512-bit (64-byte) boundary: append 0x80, then zeros, then 64-bit LE length
  let padBytes = 64 - ((msgLen + 9) % 64);
  if (padBytes < 0) padBytes += 64;
  const totalLen = msgLen + 1 + padBytes + 8;
  const msg = newBuf(totalLen);
  msg.set(input);
  msg[msgLen] = 0x80;
const view = new DataView(msg.buffer);
  view.setUint32(totalLen - 8, bitLen >>> 0, true);
  view.setUint32(totalLen - 4, Math.floor(bitLen / 0x100000000) >>> 0, true);

  // Initial hash values
  let a0 = 0x67452301;
  let b0 = 0xefcdab89;
  let c0 = 0x98badcfe;
  let d0 = 0x10325476;

  const S = [
    7,12,17,22, 7,12,17,22, 7,12,17,22, 7,12,17,22,
    5, 9,14,20, 5, 9,14,20, 5, 9,14,20, 5, 9,14,20,
    4,11,16,23, 4,11,16,23, 4,11,16,23, 4,11,16,23,
    6,10,15,21, 6,10,15,21, 6,10,15,21, 6,10,15,21,
  ];

  const T = [
    0xd76aa478,0xe8c7b756,0x242070db,0xc1bdceee,
    0xf57c0faf,0x4787c62a,0xa8304613,0xfd469501,
    0x698098d8,0x8b44f7af,0xffff5bb1,0x895cd7be,
 0x6b901122,0xfd987193,0xa679438e,0x49b40821,
    0xf61e2562,0xc040b340,0x265e5a51,0xe9b6c7aa,
  0xd62f105d,0x02441453,0xd8a1e681,0xe7d3fbc8,
    0x21e1cde6,0xc33707d6,0xf4d50d87,0x455a14ed,
  0xa9e3e905,0xfcefa3f8,0x676f02d9,0x8d2a4c8a,
  0xfffa3942,0x8771f681,0x6d9d6122,0xfde5380c,
    0xa4beea44,0x4bdecfa9,0xf6bb4b60,0xbebfbc70,
    0x289b7ec6,0xeaa127fa,0xd4ef3085,0x04881d05,
    0xd9d4d039,0xe6db99e5,0x1fa27cf8,0xc4ac5665,
    0xf4292244,0x432aff97,0xab9423a7,0xfc93a039,
    0x655b59c3,0x8f0ccc92,0xffeff47d,0x85845dd1,
0x6fa87e4f,0xfe2ce6e0,0xa3014314,0x4e0811a1,
    0xf7537e82,0xbd3af235,0x2ad7d2bb,0xeb86d391,
  ];

  const add32 = (x: number, y: number) => (x + y) | 0;
  const rotLeft = (x: number, n: number) => (x << n) | (x >>> (32 - n));
  const msgView = new DataView(msg.buffer);

  for (let i = 0; i < totalLen; i += 64) {
    const M: number[] = [];
    for (let j = 0; j < 16; j++) {
      M[j] = msgView.getUint32(i + j * 4, true);
    }
 let a = a0, b = b0, c = c0, d = d0;
    for (let j = 0; j < 64; j++) {
      let f: number, g: number;
      if (j < 16) {
        f = (b & c) | (~b & d); g = j;
      } else if (j < 32) {
     f = (d & b) | (~d & c); g = (5 * j + 1) % 16;
      } else if (j < 48) {
        f = b ^ c ^ d; g = (3 * j + 5) % 16;
   } else {
        f = c ^ (b | ~d); g = (7 * j) % 16;
      }
      const temp = d;
      d = c; c = b;
      b = add32(b, rotLeft(add32(add32(a, f), add32(T[j], M[g])), S[j]));
    a = temp;
    }
    a0 = add32(a0, a);
    b0 = add32(b0, b);
    c0 = add32(c0, c);
    d0 = add32(d0, d);
  }

  const result = newBuf(16);
  const rv = new DataView(result.buffer);
  rv.setUint32(0,  a0, true);
  rv.setUint32(4,  b0, true);
  rv.setUint32(8,  c0, true);
  rv.setUint32(12, d0, true);
  return result;
}

// ---------------------------------------------------------------------------
// EVP_BytesToKey (synchronous)
// ---------------------------------------------------------------------------

/**
 * OpenSSL EVP_BytesToKey — MD5-based key+IV derivation.
 * Matches Ruby's `OpenSSL::Cipher#pkcs5_keyivgen` (PBEWithMD5AndDES scheme).
 */
function evpBytesToKey(
  pass: string,
  salt: Uint8Array,
  keyLen: number,
  ivLen: number,
  iterations: number,
): { key: Uint8Array<ArrayBuffer>; iv: Uint8Array<ArrayBuffer> } {
  const enc = new TextEncoder();
  const passBytes = enc.encode(pass);
  const needed = keyLen + ivLen;
  const output = newBuf(needed);
  let filled = 0;
  let prev: Uint8Array<ArrayBuffer> = newBuf(0);

  while (filled < needed) {
    const combined = newBuf(prev.length + passBytes.length + salt.length);
combined.set(prev, 0);
    combined.set(passBytes, prev.length);
    combined.set(salt, prev.length + passBytes.length);

    let data: Uint8Array<ArrayBuffer> = combined;
  for (let i = 0; i < iterations; i++) {
      data = md5(data);
    }
    prev = data;

    const take = Math.min(data.length, needed - filled);
    output.set(data.slice(0, take), filled);
    filled += take;
  }

  return {
    key: output.slice(0, keyLen) as Uint8Array<ArrayBuffer>,
iv:  output.slice(keyLen, keyLen + ivLen) as Uint8Array<ArrayBuffer>,
  };
}

// ---------------------------------------------------------------------------
// DES-CBC (synchronous, FIPS 46-3)
// ---------------------------------------------------------------------------

const IP  = [58,50,42,34,26,18,10,2,60,52,44,36,28,20,12,4,62,54,46,38,30,22,14,6,64,56,48,40,32,24,16,8,57,49,41,33,25,17,9,1,59,51,43,35,27,19,11,3,61,53,45,37,29,21,13,5,63,55,47,39,31,23,15,7];
const IP2 = [40,8,48,16,56,24,64,32,39,7,47,15,55,23,63,31,38,6,46,14,54,22,62,30,37,5,45,13,53,21,61,29,36,4,44,12,52,20,60,28,35,3,43,11,51,19,59,27,34,2,42,10,50,18,58,26,33,1,41,9,49,17,57,25];
const E   = [32,1,2,3,4,5,4,5,6,7,8,9,8,9,10,11,12,13,12,13,14,15,16,17,16,17,18,19,20,21,20,21,22,23,24,25,24,25,26,27,28,29,28,29,30,31,32,1];
const P   = [16,7,20,21,29,12,28,17,1,15,23,26,5,18,31,10,2,8,24,14,32,27,3,9,19,13,30,6,22,11,4,25];
const PC1 = [57,49,41,33,25,17,9,1,58,50,42,34,26,18,10,2,59,51,43,35,27,19,11,3,60,52,44,36,63,55,47,39,31,23,15,7,62,54,46,38,30,22,14,6,61,53,45,37,29,21,13,5,28,20,12,4];
const PC2 = [14,17,11,24,1,5,3,28,15,6,21,10,23,19,12,4,26,8,16,7,27,20,13,2,41,52,31,37,47,55,30,40,51,45,33,48,44,49,39,56,34,53,46,42,50,36,29,32];
const LS  = [1,1,2,2,2,2,2,2,1,2,2,2,2,2,2,1];

const S_BOXES: number[][] = [
  [14,4,13,1,2,15,11,8,3,10,6,12,5,9,0,7,0,15,7,4,14,2,13,1,10,6,12,11,9,5,3,8,4,1,14,8,13,6,2,11,15,12,9,7,3,10,5,0,15,12,8,2,4,9,1,7,5,11,3,14,10,0,6,13],
  [15,1,8,14,6,11,3,4,9,7,2,13,12,0,5,10,3,13,4,7,15,2,8,14,12,0,1,10,6,9,11,5,0,14,7,11,10,4,13,1,5,8,12,6,9,3,2,15,13,8,10,1,3,15,4,2,11,6,7,12,0,5,14,9],
  [10,0,9,14,6,3,15,5,1,13,12,7,11,4,2,8,13,7,0,9,3,4,6,10,2,8,5,14,12,11,15,1,13,6,4,9,8,15,3,0,11,1,2,12,5,10,14,7,1,10,13,0,6,9,8,7,4,15,14,3,11,5,2,12],
  [7,13,14,3,0,6,9,10,1,2,8,5,11,12,4,15,13,8,11,5,6,15,0,3,4,7,2,12,1,10,14,9,10,6,9,0,12,11,7,13,15,1,3,14,5,2,8,4,3,15,0,6,10,1,13,8,9,4,5,11,12,7,2,14],
  [2,12,4,1,7,10,11,6,8,5,3,15,13,0,14,9,14,11,2,12,4,7,13,1,5,0,15,10,3,9,8,6,4,2,1,11,10,13,7,8,15,9,12,5,6,3,0,14,11,8,12,7,1,14,2,13,6,15,0,9,10,4,5,3],
  [12,1,10,15,9,2,6,8,0,13,3,4,14,7,5,11,10,15,4,2,7,12,9,5,6,1,13,14,0,11,3,8,9,14,15,5,2,8,12,3,7,0,4,10,1,13,11,6,4,3,2,12,9,5,15,10,11,14,1,7,6,0,8,13],
  [4,11,2,14,15,0,8,13,3,12,9,7,5,10,6,1,13,0,11,7,4,9,1,10,14,3,5,12,2,15,8,6,1,4,11,13,12,3,7,14,10,15,6,8,0,5,9,2,6,11,13,8,1,4,10,7,9,5,0,15,14,2,3,12],
  [13,2,8,4,6,15,11,1,10,9,3,14,5,0,12,7,1,15,13,8,10,3,7,4,12,5,6,11,0,14,9,2,7,11,4,1,9,12,14,2,0,6,10,13,15,3,5,8,2,1,14,7,4,10,8,13,15,12,9,0,3,5,6,11],
];

function permute(input: number[], table: number[], outLen: number): number[] {
  const out = new Array<number>(outLen).fill(0);
  for (let i = 0; i < outLen; i++) {
    out[i] = input[table[i] - 1];
  }
  return out;
}

function bytesToBits(bytes: Uint8Array): number[] {
  const bits: number[] = [];
  for (const b of bytes) {
    for (let i = 7; i >= 0; i--) {
 bits.push((b >> i) & 1);
    }
  }
  return bits;
}

function bitsToBytes(bits: number[]): Uint8Array<ArrayBuffer> {
  const bytes = newBuf(bits.length / 8);
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j++) {
      byte = (byte << 1) | bits[i + j];
    }
    bytes[i / 8] = byte;
  }
  return bytes;
}

function rotateLeft(arr: number[], n: number): number[] {
  return [...arr.slice(n), ...arr.slice(0, n)];
}

function generateSubkeys(keyBytes: Uint8Array): number[][] {
  const keyBits = bytesToBits(keyBytes);
  const kp = permute(keyBits, PC1, 56);
  let c = kp.slice(0, 28);
  let d = kp.slice(28, 56);
  const subkeys: number[][] = [];
  for (let i = 0; i < 16; i++) {
    c = rotateLeft(c, LS[i]);
    d = rotateLeft(d, LS[i]);
    subkeys.push(permute([...c, ...d], PC2, 48));
  }
  return subkeys;
}

function desFeistel(right: number[], subkey: number[]): number[] {
  const expanded = permute(right, E, 48);
  const xored = expanded.map((b, i) => b ^ subkey[i]);
  const sboxOut: number[] = [];
  for (let i = 0; i < 8; i++) {
    const chunk = xored.slice(i * 6, i * 6 + 6);
    const row = (chunk[0] << 1) | chunk[5];
    const col = (chunk[1] << 3) | (chunk[2] << 2) | (chunk[3] << 1) | chunk[4];
    const val = S_BOXES[i][row * 16 + col];
    for (let j = 3; j >= 0; j--) {
      sboxOut.push((val >> j) & 1);
    }
  }
  return permute(sboxOut, P, 32);
}

function desDecryptBlock(block: Uint8Array, subkeys: number[][]): Uint8Array<ArrayBuffer> {
  const bits = bytesToBits(block);
  const ipBits = permute(bits, IP, 64);
  let l = ipBits.slice(0, 32);
  let r = ipBits.slice(32, 64);
  for (let i = 15; i >= 0; i--) {
    const newR = l.map((b, idx) => b ^ desFeistel(r, subkeys[i])[idx]);
  l = r;
 r = newR;
  }
  const combined = [...r, ...l];
  return bitsToBytes(permute(combined, IP2, 64));
}

/**
 * Synchronous DES-CBC decryption with PKCS#5 unpadding.
 */
function desCbcDecrypt(
  ciphertext: Uint8Array,
  key: Uint8Array,
  iv: Uint8Array,
): Uint8Array<ArrayBuffer> {
  const subkeys = generateSubkeys(key);
  const blocks = ciphertext.length / 8;
  const plaintext = newBuf(ciphertext.length);
  let prevBlock: Uint8Array = iv;

  for (let i = 0; i < blocks; i++) {
    const block = ciphertext.slice(i * 8, i * 8 + 8);
    const decrypted = desDecryptBlock(block, subkeys);
    for (let j = 0; j < 8; j++) {
      plaintext[i * 8 + j] = decrypted[j] ^ prevBlock[j];
    }
    prevBlock = block;
  }

  // PKCS#5 unpadding
  const padLen = plaintext[plaintext.length - 1];
  if (padLen < 1 || padLen > 8) {
    throw new Error(`Invalid PKCS#5 padding byte: ${padLen}`);
  }
  return plaintext.slice(0, plaintext.length - padLen) as Uint8Array<ArrayBuffer>;
}

// ---------------------------------------------------------------------------
// Decryption constants
// ---------------------------------------------------------------------------

/** Fixed passkey used by the Rails SsnEncryption helper. */
const SSN_PASSKEY = 'Fusce vitae nisl a eros placerat tincidunt sed nec orci.';

/** Fixed salt used by the Rails PBEWithMD5AndDES key derivation. */
const SSN_SALT = new Uint8Array(new ArrayBuffer(8));
(() => {
  const s = [0xa4, 0x9b, 0xc8, 0x38, 0x59, 0x35, 0xe1, 0x07];
  s.forEach((v, i) => { SSN_SALT[i] = v; });
})();

/** Number of MD5 iterations in the Rails PBEWithMD5AndDES derivation. */
const SSN_KDF_ITERATIONS = 17;

/**
 * Converts a hex-encoded string (e.g. "0AAFFEF6…") into a Uint8Array.
 */
function hexToBytes(hex: string): Uint8Array<ArrayBuffer> {
  const clean = hex.trim().replace(/\s+/g, '');
  if (clean.length % 2 !== 0) {
    throw new Error(`Invalid hex string length: ${clean.length}`);
  }
  const bytes = newBuf(clean.length / 2);
  for (let i = 0; i < clean.length; i += 2) {
    bytes[i / 2] = parseInt(clean.slice(i, i + 2), 16);
  }
  return bytes;
}

// ---------------------------------------------------------------------------
// Public decryption API
// ---------------------------------------------------------------------------

/**
 * Synchronously decrypts a hex-encoded DES-CBC SSN ciphertext using the
 * Rails `SsnEncryption.decode` scheme (PBEWithMD5AndDES, 17 iterations).
 *
 * @param encrypted - The raw `ssn_encrypted` hex value from the API.
 * @returns `{ plain: string | null; error: boolean }`
 *   `plain` is the decrypted plain-text SSN, or null on empty input/error.
 *   `error` is true only when decryption itself failed.
 */
export function decryptSSN(
  encrypted: string | null | undefined,
): { plain: string | null; error: boolean } {
  if (!encrypted) {
 return { plain: null, error: false };
  }

  try {
    const cipherBytes = hexToBytes(encrypted);
    const { key, iv } = evpBytesToKey(SSN_PASSKEY, SSN_SALT, 8, 8, SSN_KDF_ITERATIONS);
    const plainBytes = desCbcDecrypt(cipherBytes, key, iv);
    const plain = new TextDecoder('utf-8').decode(plainBytes);
    return { plain, error: false };
  } catch (err) {
    logger.error('[ssnUtils] Failed to decrypt SSN:', err);
    return { plain: null, error: true };
  }
}

// ---------------------------------------------------------------------------
// Module-level locale singleton
// ---------------------------------------------------------------------------

/**
 * Module-level site locale, resolved once at startup from runtime config.
 * Defaults to "fi" so that the safer (more privacy-preserving) masking
 * applies until an explicit locale is configured.
 */
let _siteLocale: string = 'fi';

/**
 * Sets the site locale used by {@link getObfuscatedSSN}.
 *
 * Call this once from the RuntimeConfigProvider (or App root) after the
 * runtime config has been fetched:
 *
 * ```ts
 * import { setSiteLocale } from '@/shared/utils/ssnUtils';
 * const locale = getConfigValue('general.locale') ?? 'fi';
 * setSiteLocale(String(locale));
 * ```
 */
export function setSiteLocale(locale: string): void {
  if (locale && locale.trim()) {
    _siteLocale = locale.trim();
  }
}

/** Returns the currently active site locale. Primarily useful for testing. */
export function getSiteLocale(): string {
  return _siteLocale;
}

// ---------------------------------------------------------------------------
// Masking
// ---------------------------------------------------------------------------

/**
 * Applies masking to a **plain-text** SSN string.
 *
 * Masks all characters except the last 4 with `*`.
 * Values of 4 characters or shorter are shown as-is.
 *
 *   "123456789"  → "*****6789"
 *   "1234"       → "1234"
 *   ""    → ""
 *
 * The `locale` parameter is accepted for API compatibility and future
 * locale-specific divergence but does not currently affect behaviour.
 *
 * @param plain  - Decrypted plain-text SSN. **MUST NOT** be forwarded to the
 *   UI directly; only the return value of this function renders.
 * @param locale - Site locale (reserved for future use).
 */
export function maskSSNByLocale(plain: string): string {
  if (!plain) return '';
  if (plain.length <= 4) return plain;
  return '*'.repeat(plain.length - 4) + plain.slice(-4);
}

// ---------------------------------------------------------------------------
// Composed pipeline (synchronous)
// ---------------------------------------------------------------------------

/**
 * Full SSN obfuscation pipeline: decrypt → mask.
 *
 * This is the **only** function that data-mapping sites (e.g. clientsService)
 * should call. Guarantees:
 *  1. The plain SSN is **never** returned to the caller.
 *  2. Decryption failures return {@link SSN_DECRYPT_ERROR_MARKER} (`"<<ERROR>>"`).
 *  3. Empty / null inputs return `""`.
 *
 * @param encrypted - Raw `ssn_encrypted` hex string from the API.
 */
export function getObfuscatedSSN(
  encrypted: string | null | undefined,
): string {
  const { plain, error } = decryptSSN(encrypted);

  if (error) {
    return SSN_DECRYPT_ERROR_MARKER;
  }

  if (!plain) return '';

  return maskSSNByLocale(plain);
}
